import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface ProjectTypeData {
  structuralType: string;
  customStructuralType?: string;
  placeId: string;
  placeDescription?: string;
  projectId?: string;
  projectTypeId?: string;
}

export interface ScopeOfWorkOption {
  id: string;
  label: string;
  value: string;
  description: string;
  imageUrl: string;
}

export interface ProjectSectionOption {
  projectTaskId: string;
  label: string;
  shortLabel: string;
  value: string;
  projectScope: string;
  imageUrl: string;
}

export interface ProjectScopeData {
  scopeOfWork: string;
  projectScopeId: string;
  projectSection: string; // Single selected project section ID
  projectSectionValue: string; // Display value for selected section
}

export interface ProjectDetailsData {
  dynamicFields: Record<string, any>; // User selections for dynamic fields
  additionalNotes: string; // Additional notes text
  uploadedFiles: any[]; // Uploaded files with upload response data
}

export interface ProjectCreationStore {
  // Project Type Data
  projectType: ProjectTypeData;

  // Project Scope Data
  projectScope: ProjectScopeData;

  // Project Details Data
  projectDetails: ProjectDetailsData;

  // Project Type Actions
  setStructuralType: (type: string) => void;
  setCustomStructuralType: (type: string) => void;
  setPlace: (placeId: string, description: string) => void;
  clearPlace: () => void;
  setProjectId: (id: string) => void;
  setProjectTypeId: (id: string) => void;
  updateProjectType: (data: Partial<ProjectTypeData>) => void;
  clearProjectTypeData: () => void;

  // Project Scope Actions
  setScopeOfWork: (scope: string, scopeId: string) => void;
  setProjectSection: (sectionId: string, sectionValue: string) => void;
  updateProjectScope: (data: Partial<ProjectScopeData>) => void;
  clearProjectScopeData: () => void;

  // Project Details Actions
  setDynamicField: (key: string, value: any) => void;
  setAdditionalNotes: (notes: string) => void;
  setUploadedFiles: (files: any[]) => void;
  updateProjectDetails: (data: Partial<ProjectDetailsData>) => void;
  clearProjectDetailsData: () => void;

  // Utility
  initializeFromStorage: () => void;
  isProjectTypeComplete: () => boolean;
  isProjectScopeComplete: () => boolean;
  isProjectDetailsComplete: () => boolean;
  clearAllData: () => void;
}

const STORAGE_KEY = "plana-project-creation";
const DEFAULT_PROJECT_TYPE: ProjectTypeData = {
  structuralType: "",
  customStructuralType: "",
  placeId: "",
  placeDescription: "",
  projectId: "",
  projectTypeId: "",
};

const DEFAULT_PROJECT_SCOPE: ProjectScopeData = {
  scopeOfWork: "",
  projectScopeId: "",
  projectSection: "",
  projectSectionValue: "",
};

const DEFAULT_PROJECT_DETAILS: ProjectDetailsData = {
  dynamicFields: {},
  additionalNotes: "",
  uploadedFiles: [],
};

export const useProjectCreationStore = create<ProjectCreationStore>()(
  persist(
    (set, get) => ({
      // Initial state
      projectType: DEFAULT_PROJECT_TYPE,
      projectScope: DEFAULT_PROJECT_SCOPE,
      projectDetails: DEFAULT_PROJECT_DETAILS,

      // Actions
      setStructuralType: (type: string) => {
        set((state) => ({
          projectType: {
            ...state.projectType,
            structuralType: type,
          },
        }));
      },

      setCustomStructuralType: (type: string) => {
        set((state) => ({
          projectType: {
            ...state.projectType,
            customStructuralType: type,
          },
        }));
      },

      setPlace: (placeId: string, description: string) => {
        set((state) => ({
          projectType: {
            ...state.projectType,
            placeId,
            placeDescription: description,
          },
        }));
      },

      clearPlace: () => {
        set((state) => ({
          projectType: {
            ...state.projectType,
            placeId: "",
            placeDescription: "",
          },
        }));
      },

      setProjectId: (id: string) => {
        set((state) => ({
          projectType: {
            ...state.projectType,
            projectId: id,
          },
        }));
      },
      setProjectTypeId: (id: string) => {
        set((state) => ({
          projectType: {
            ...state.projectType,
            projectTypeId: id,
          },
        }));
      },

      updateProjectType: (data: Partial<ProjectTypeData>) => {
        set((state) => ({
          projectType: {
            ...state.projectType,
            ...data,
          },
        }));
      },

      clearProjectTypeData: () => {
        set({
          projectType: DEFAULT_PROJECT_TYPE,
        });
      },

      // Project Scope Actions
      setScopeOfWork: (scope: string, scopeId: string) => {
        set((state) => ({
          projectScope: {
            ...state.projectScope,
            scopeOfWork: scope,
            projectScopeId: scopeId,
          },
        }));
      },

      setProjectSection: (sectionId: string, sectionValue: string) => {
        set((state) => ({
          projectScope: {
            ...state.projectScope,
            projectSection: sectionId,
            projectSectionValue: sectionValue,
          },
        }));
      },

      updateProjectScope: (data: Partial<ProjectScopeData>) => {
        set((state) => ({
          projectScope: {
            ...state.projectScope,
            ...data,
          },
        }));
      },

      clearProjectScopeData: () => {
        set({
          projectScope: DEFAULT_PROJECT_SCOPE,
        });
      },

      // Project Details Actions
      setDynamicField: (key: string, value: any) => {
        set((state) => ({
          projectDetails: {
            ...state.projectDetails,
            dynamicFields: {
              ...state.projectDetails.dynamicFields,
              [key]: value,
            },
          },
        }));
      },

      setAdditionalNotes: (notes: string) => {
        set((state) => ({
          projectDetails: {
            ...state.projectDetails,
            additionalNotes: notes,
          },
        }));
      },

      setUploadedFiles: (files: any[]) => {
        set((state) => ({
          projectDetails: {
            ...state.projectDetails,
            uploadedFiles: files,
          },
        }));
      },

      updateProjectDetails: (data: Partial<ProjectDetailsData>) => {
        set((state) => ({
          projectDetails: {
            ...state.projectDetails,
            ...data,
          },
        }));
      },

      clearProjectDetailsData: () => {
        set({
          projectDetails: DEFAULT_PROJECT_DETAILS,
        });
      },

      initializeFromStorage: () => {
        // This is handled automatically by Zustand persist middleware
        // But we can add custom initialization logic here if needed
        const state = get();
      },

      isProjectTypeComplete: () => {
        const { projectType } = get();
        const hasStructuralType = Boolean(projectType.structuralType);
        const hasPlace = Boolean(projectType.placeId?.trim());
        const hasCustomType =
          projectType.structuralType !== "other" ||
          Boolean(projectType.customStructuralType?.trim());

        return hasStructuralType && hasPlace && hasCustomType;
      },

      isProjectScopeComplete: () => {
        const { projectScope } = get();
        const hasScope = Boolean(projectScope.scopeOfWork);
        const hasProjectScopeId = Boolean(projectScope.projectScopeId);
        const hasSection = Boolean(projectScope.projectSection);

        return hasScope && hasProjectScopeId && hasSection;
      },

      isProjectDetailsComplete: () => {
        const { projectDetails } = get();
        // Project details is considered complete if there are any dynamic fields filled
        // or if additional notes are provided
        const hasDynamicFields =
          Object.keys(projectDetails.dynamicFields).length > 0;
        const hasAdditionalNotes = Boolean(
          projectDetails.additionalNotes?.trim()
        );

        return hasDynamicFields || hasAdditionalNotes;
      },

      clearAllData: () => {
        set({
          projectType: DEFAULT_PROJECT_TYPE,
          projectScope: DEFAULT_PROJECT_SCOPE,
          projectDetails: DEFAULT_PROJECT_DETAILS,
        });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        projectType: state.projectType,
        projectScope: state.projectScope,
        projectDetails: state.projectDetails,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.initializeFromStorage();
        }
      },
    }
  )
);

// Simple initialization function
export const initializeStore = () => {
  const store = useProjectCreationStore.getState();
  store.initializeFromStorage();
};

// Utility hook for easy access to project type data
export const useProjectTypeData = () => {
  const projectType = useProjectCreationStore((state) => state.projectType);
  const updateProjectType = useProjectCreationStore(
    (state) => state.updateProjectType
  );
  const isComplete = useProjectCreationStore((state) =>
    state.isProjectTypeComplete()
  );

  return {
    projectType,
    updateProjectType,
    isComplete,
  };
};

// Utility hook for easy access to project scope data
export const useProjectScopeData = () => {
  const projectScope = useProjectCreationStore((state) => state.projectScope);
  const updateProjectScope = useProjectCreationStore(
    (state) => state.updateProjectScope
  );
  const isComplete = useProjectCreationStore((state) =>
    state.isProjectScopeComplete()
  );

  return {
    projectScope,
    updateProjectScope,
    isComplete,
  };
};

// Utility hook for easy access to project details data
export const useProjectDetailsData = () => {
  const projectDetails = useProjectCreationStore(
    (state) => state.projectDetails
  );
  const setDynamicField = useProjectCreationStore(
    (state) => state.setDynamicField
  );
  const setAdditionalNotes = useProjectCreationStore(
    (state) => state.setAdditionalNotes
  );
  const updateProjectDetails = useProjectCreationStore(
    (state) => state.updateProjectDetails
  );
  const isComplete = useProjectCreationStore((state) =>
    state.isProjectDetailsComplete()
  );

  return {
    projectDetails,
    setDynamicField,
    setAdditionalNotes,
    updateProjectDetails,
    isComplete,
  };
};
